import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, MessageBar, MessageBarActions, MessageBarBody } from '@fluentui/react-components';
import {
  DATE_UNAVAILABLE, agencyCalendarDate, formatRecordedCalendarDate, latestAcceptedOffer, personDisplayName,
  type HrWorkflowRecord, type Person, type RejectionReason, type StepInstance,
} from '@vigeo/shared';
import { fetchHrWorkflow } from '@/features/hr-workflow';
import { RejectDialog } from '@/features/review';
import { sendCommunication } from '@/features/communications';
import { dayAndTime } from '@/features/offers';
import { approveSubmission, changeLifecycle, rejectSubmission } from '@/lib/api';
import { MoveToOnboarding } from './MoveToOnboarding.js';
import { SentOnboardingItems } from './SentOnboardingItems.js';
import { OfferOutcome } from './OfferOutcome.js';
import { ReviewSignedOffer } from './ReviewSignedOffer.js';
import { outcomeCopy, reviewCopy, signedOfferSummary } from './model.js';

/**
 * WHAT HR IS TOLD when something did not go to plan, and how loudly.
 *
 * `error` is a REFUSAL: nothing happened, the row is still there, clicking again is the right next
 * move. `warning` is the opposite case — the thing HR asked for DID happen and something optional
 * beside it did not, so telling them it failed would invite a second click that must not follow.
 *
 * The two are also announced differently, which is the only part of the distinction a test in jsdom
 * can see: Fluent's own `MessageBar` renders `role="group"` and announces through `useAnnounce`,
 * which is inert without an announce provider. So the notice carries its own live region —
 * `alert` (assertive) for a refusal, `status` (polite) for a warning — or a screen-reader user gets
 * silence at exactly the moment this component exists to break silence.
 */
interface Notice {
  /**
   * `success` added for 2.5's resend (#373). It announces as `status`, like `warning` — polite, not
   * assertive: a queued email is worth confirming and not worth interrupting a screen reader for.
   * Only `error` is assertive, and only a refusal is an error.
   */
  readonly kind: 'warning' | 'error' | 'success';
  readonly text: string;
}

/**
 * Which of the two Offer-and-stage rows this candidate is on, and the actions that move them.
 *
 * WHICH ROW IS DECIDED BY THE DATA, not by a tab. 2.1 and 2.2 are consecutive states of one
 * section, so the section asks the same two questions the server asks:
 *
 *   - is there a signed offer step still waiting on HR?  → 2.1, Review the signed offer
 *   - has `acceptedOffer` accepted one, and are they still a candidate? → 2.2, Move to Onboarding
 *
 * `acceptedOffer` is the ADR-011 §4 gate itself — the same function `canBeginOnboarding` calls on
 * the server. Reading it here rather than re-deriving "is it signed" is what stops this screen
 * offering a move the API will refuse, or hiding one it would allow.
 *
 * Renders nothing at all when neither applies — EXCEPT a notice, which outlives both rows on
 * purpose (see `notice` below). The Hiring section keeps its existing panel and stage control
 * underneath, so a candidate with no offer yet is unaffected.
 */
export function OfferStageSection({ person, canEdit, roleLabel, onGoToDocuments }: {
  readonly person: Person;
  readonly canEdit: boolean;
  readonly roleLabel: string | null;
  /**
   * Opens Documents & checklist (3.1) from the sent window (2.4). Optional: a caller that renders
   * this outside the person record has nowhere to send them, and 2.4 then shows Close alone.
   */
  readonly onGoToDocuments?: () => void;
}) {
  const client = useQueryClient();
  const [sendingBack, setSendingBack] = useState(false);
  /**
   * WHAT WENT WRONG, AND WHICH KIND OF WRONG — set together, by the callback that knows.
   *
   * The kind was previously derived at render time from `move.isSuccess`, which TanStack Query only
   * turns on AFTER `onSuccess` has resolved — and `onSuccess` here awaits `refresh()`, five
   * invalidations with real refetches behind them. So for the whole of that window, which is exactly
   * when HR reads it, a move that HAD happened was dressed as a red error: the "tell HR the move did
   * not work and invite them to click again" outcome the mutation below says must not follow.
   */
  const [failure, setFailure] = useState<Notice | null>(null);
  /**
   * 2.4, the sent window (#373). TRANSIENT: it is shown to whoever just sent, and 2.5 is the durable
   * record. It holds the warning too, so "the forms went out, the email did not" is said on the
   * screen that confirms the send rather than only in the banner underneath it.
   */
  const [sent, setSent] = useState<{ readonly warning: string | null } | null>(null);
  const workflow = useQuery({ queryKey: ['hr-workflow', person.id], queryFn: () => fetchHrWorkflow(person.id) });

  const refresh = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: ['hr-workflow', person.id] }),
      client.invalidateQueries({ queryKey: ['person', person.id] }),
      client.invalidateQueries({ queryKey: ['checklist', person.id] }),
      client.invalidateQueries({ queryKey: ['review'] }),
      client.invalidateQueries({ queryKey: ['audit', person.id] }),
    ]);
  };

  const decide = useMutation({
    mutationFn: async (decision: { readonly kind: 'approve' } | { readonly kind: 'reject'; readonly reason: RejectionReason; readonly note: string; readonly candidateComment: string }) => {
      const step = awaitingStep;
      if (step === null) throw new Error('There is no signed offer waiting for review.');
      if (decision.kind === 'approve') await approveSubmission(step.id, person.id);
      else await rejectSubmission(step.id, person.id, { reason: decision.reason, note: decision.note, candidateComment: decision.candidateComment });
    },
    onSuccess: async () => { setSendingBack(false); setFailure(null); await refresh(); },
    onError: (error: Error) => setFailure({ kind: 'error', text: error.message }),
  });

  const move = useMutation({
    mutationFn: async (choice: { readonly reason: string; readonly email: boolean }) => {
      await changeLifecycle(person.id, { to: 'onboarding', reason: choice.reason });
      if (!choice.email) return null;

      /*
       * The email is a SEPARATE call, made after the move has already succeeded.
       *
       * `changeLifecycleSchema` carries no field for it, and adding one would make sending mail a
       * side effect of a state change — a click that means "move them" would also mean "write to
       * this candidate", with one audit row covering both. Two calls keep the two decisions
       * separable, which is also what lets the failure below be reported honestly.
       *
       * The template is the agency's own `welcome`, which is what the frame names ("Sends the
       * Welcome message from Follow-ups & messages"). Merge fields are rendered by the server, as
       * they are for the composer; nothing is interpolated here.
       */
      if (welcomeTemplate === null) return 'They were moved. No Welcome template is set up, so no email was sent — add one in Agency settings.';
      try {
        await sendWelcomeEmail(crypto.randomUUID());
        return null;
      } catch (error) {
        // THE MOVE ALREADY HAPPENED. Reporting this as a plain failure would tell HR the move did
        // not work and invite them to click again, which is the one thing that must not follow.
        return `They were moved to Onboarding, but the welcome email could not be queued: ${(error as Error).message}`;
      }
    },
    // The kind is decided HERE, where it is known, and never re-derived from mutation status.
    onSuccess: async (warning) => {
      setFailure(warning === null ? null : { kind: 'warning', text: warning });
      // BEFORE the refresh: `refresh()` flips the lifecycle to onboarding and re-renders this
      // section down its past-candidate branch, so setting this after would race the unmount.
      setSent({ warning });
      await refresh();
    },
    onError: (error: Error) => setFailure({ kind: 'error', text: error.message }),
  });

  /** The one way a notice leaves the screen. Both the Dismiss action and row 2.2's Cancel use it. */
  const dismiss = () => setFailure(null);

  /*
   * THE NOTICE OUTLIVES THE ROW THAT RAISED IT, which is why it is built here — above every early
   * return — rather than inside either row.
   *
   * A successful move flips `person.lifecycle` to `onboarding`, and that is the very condition row
   * 2.2 renders on. While the notice was a child of that row, the `refresh()` the move itself
   * performs unmounted the warning within a frame of painting it: HR who ticked "Email <name> that
   * their onboarding steps are ready", hit a refused `sendCommunication`, and read a sub-second
   * flash had every reason to believe the welcome email went out. It had not. The warning now
   * survives the transition and is cleared only when HR dismisses it.
   */
  /**
   * NOT WHILE 2.4 IS OPEN, or the same sentence is on screen twice.
   *
   * `move`'s warning goes to both the sent window and this banner: the window is the right place to
   * say "the forms went out, the email did not" at the moment of sending, and the banner is the
   * durable version for after it closes. Rendering both at once put "the welcome email could not be
   * queued" on screen twice — two tests found it immediately by matching multiple elements.
   *
   * The banner is suppressed rather than the window's copy dropped, because the confirmation of a
   * send should say what actually happened. Closing 2.4 brings the banner back, and `failure` is
   * untouched, so nothing is lost if the window is dismissed without being read.
   */
  const notice = failure === null || sent !== null ? null : (
    // The live region is this component's, not Fluent's — see `Notice`. It is also the only part of
    // the warning/error decision a jsdom test can assert, which is why both read `failure.kind`.
    <div role={failure.kind === 'error' ? 'alert' : 'status'}>
      <MessageBar intent={failure.kind} politeness={failure.kind === 'error' ? 'assertive' : 'polite'}>
        <MessageBarBody>{failure.text}</MessageBarBody>
        <MessageBarActions>
          <Button onClick={dismiss}>Dismiss</Button>
        </MessageBarActions>
      </MessageBar>
    </div>
  );

  const data = workflow.data;

  /**
   * THE FIRST-DAY LINE, and it is three sentences rather than one because it is three facts.
   *
   * `hireDate` is "First day / hire date" on the details form and is required BEFORE activation, so
   * for the people 2.5 is built for it is normally a day that has not arrived. "Hired on <next
   * Monday>" asserts an event that has not happened, and a review of #396 found the fixture for this
   * screen encoding exactly that. Past or today keeps Paul's wording; a future date says "First
   * day"; an unreadable stored value says so instead of printing the formatter's sentinel inside a
   * sentence, which is how "Hired on Date unavailable" reached the screen.
   *
   * The comparison is string-wise on `YYYY-MM-DD`, which is correct for that format, and against the
   * AGENCY's today rather than the viewer's — an HR user in another timezone must not see a
   * different tense for the same person.
   */
  const hireDateLine = (() => {
    if (person.hireDate === null) return outcomeCopy.hireDateNotSet;
    const formatted = formatRecordedCalendarDate(person.hireDate);
    if (formatted === DATE_UNAVAILABLE) return outcomeCopy.hireDateUnreadable;
    // `.slice(0, 10)` because a stored value may carry a time: `'2026-09-30T00:00:00.000Z'` is
    // lexicographically GREATER than `'2026-09-30'`, so comparing them whole reads the first day as
    // still to come on the very day it arrives. `formatRecordedCalendarDate` slices for the same
    // reason one line above.
    return person.hireDate.slice(0, 10) > agencyCalendarDate(new Date().toISOString())
      ? outcomeCopy.firstDay(formatted)
      : outcomeCopy.hiredOn(formatted);
  })();

  /**
   * ONE DEFINITION OF "THE WELCOME EMAIL", used by the move (2.3) and the resend (2.5).
   *
   * These were about to be two copies of the same four lines, and the failure mode of that is
   * specific: a template id or a field name changes in one and the other keeps sending the old
   * shape, which nobody notices because both still "send something". Each caller still phrases its
   * OWN failure text — the move's wording is about a move that already happened, and the resend's is
   * not — because that part genuinely differs.
   */
  const welcomeTemplate = data?.settings.emailTemplates.find((template) => template.id === 'welcome') ?? null;
  const sendWelcomeEmail = async (operationId: string) => {
    if (welcomeTemplate === null) throw new Error('No Welcome template is set up.');
    await sendCommunication(person.id, {
      operationId,
      subject: welcomeTemplate.subject,
      body: welcomeTemplate.text,
    });
  };

  /**
   * ONE ID PER INTENDED SEND — the caller's half of the server's idempotency, and the reason it is
   * state rather than a `crypto.randomUUID()` inside the send.
   *
   * `sendCommunication` is documented as idempotent on `operationId`: "every id … is derived from
   * it, so a retried click meets the record its first attempt wrote." A fresh uuid per invocation
   * hands the server two unrelated requests and it has nothing to collapse on, so two clicks are two
   * emails to a real person. A review of #396 demonstrated exactly that, twice — inside one tick,
   * and again after the mutation settled while the button was briefly re-enabled. `isPending` cannot
   * close either window, which the neighbouring 2.2 fix (#395) had already learned the hard way.
   *
   * Re-armed on success, NOT held for the session: a deliberate second send an hour later is a
   * second email and must not be silently swallowed as a duplicate of the first.
   */
  const [welcomeOperationId, setWelcomeOperationId] = useState(() => crypto.randomUUID());

  /**
   * 2.5's Resend Welcome email. Deliberately NOT part of any lifecycle change — it sends one email
   * and moves nobody, so it has its own mutation and its own notice rather than borrowing the move's.
   */
  const resend = useMutation({
    mutationFn: () => sendWelcomeEmail(welcomeOperationId),
    onMutate: () => setFailure(null),
    onSuccess: () => {
      setFailure({ kind: 'success', text: outcomeCopy.resent });
      setWelcomeOperationId(crypto.randomUUID());
    },
    onError: (error: Error) => setFailure({ kind: 'error', text: `The Welcome email could not be queued: ${error.message}` }),
  });

  // Still the notice, defensively: a warning raised by a move must not depend on a second read
  // succeeding. React Query keeps the last data through a refetch, so a move cannot actually land
  // here — but `return null` would be a silent trapdoor if that ever stopped being true.
  if (data === undefined) return notice;

  const offers = data.records.filter((record): record is Extract<HrWorkflowRecord, { kind: 'offer' } | { kind: 'existing-offer' }> =>
    record.kind === 'offer' || record.kind === 'existing-offer');

  // A step the candidate has signed and HR has not yet decided on. `submitted` is the only state
  // that means that; `approved` is terminal and `rejected` is already back with the candidate.
  const awaitingStep: StepInstance | null = offers.reduce<StepInstance | null>((found, record) => {
    if (found !== null || record.kind !== 'offer' || record.instanceId === null) return found;
    return data.offerSteps.find((step) => step.id === record.instanceId && step.state === 'submitted') ?? null;
  }, null);
  const awaitingRecord = awaitingStep === null ? null
    : offers.find((record) => record.kind === 'offer' && record.instanceId === awaitingStep.id) ?? null;

  /*
   * THE GATE, READ AS A BOOLEAN — which is what `acceptedOffer` returns.
   *
   * This was `acceptedOffer(...) !== null`, and `boolean !== null` is always true, so `find`
   * returned the FIRST offer whatever its state and the ADR-011 §4 gate above was not applied at
   * all. A candidate whose only offer was an unsigned draft was told "The signed offer is approved
   * and filed" and offered a Move to Onboarding that `canBeginOnboarding` then refused.
   *
   * TypeScript cannot see it: comparing a `boolean` with `null` is a legal comparison and `tsc
   * --strict` passes, so nothing but this line's own shape says whether the gate runs.
   * `HrWorkflowPanel.tsx` has always read the same call as a plain truthy check; that disagreement
   * between the two call sites was the only visible trace. Keep them the same shape.
   */
  // WHICH one, when there are two: the latest accepted, from the one shared helper (so 2.5 and the
  // "Draft a new offer" seed in `offerStageState` cannot disagree). It applies the same gate.
  const accepted = latestAcceptedOffer(offers, data.offerSteps, data.candidacySince);
  const acceptedStep = accepted?.kind === 'offer' && accepted.instanceId !== null
    ? data.offerSteps.find((step) => step.id === accepted.instanceId) ?? null
    : null;

  const firstName = person.name.preferredName || person.name.givenName || personDisplayName(person.name);

  /**
   * 2.4, rendered in BOTH branches below on purpose.
   *
   * A successful send flips the lifecycle to onboarding, so this section re-renders down its
   * past-candidate branch — but only once `refresh()` lands. Rendering the window in one branch
   * would make it flicker or vanish depending on which side of that refresh the user is on, and the
   * whole point of it is the link into Row 3.
   *
   * NAVIGATION IS INJECTED, not performed here. Reaching for `useNavigate` made this leaf component
   * require a Router — four section tests failed on it immediately, and rightly: a feature component
   * that cannot render without routing context is harder to test than the thing it navigates to is
   * worth. `PersonPage` already owns `selectSection`, so it passes it in.
   */
  const sentWindow = sent === null ? null : (
    <SentOnboardingItems
      open
      firstName={firstName}
      warning={sent.warning}
      onClose={() => setSent(null)}
      onGoToDocuments={
        onGoToDocuments === undefined
          ? undefined
          : () => {
              setSent(null);
              onGoToDocuments();
            }
      }
    />
  );
  const busy = decide.isPending || move.isPending;

  if (awaitingStep !== null && awaitingRecord?.kind === 'offer') {
    return (
      <>
        {notice}
        <ReviewSignedOffer
          record={awaitingRecord}
          step={awaitingStep}
          firstName={firstName}
          canEdit={canEdit}
          busy={busy}
          onApprove={() => decide.mutate({ kind: 'approve' })}
          onSendBack={() => setSendingBack(true)}
        />
        <RejectDialog
          open={sendingBack}
          itemKey={awaitingStep.id}
          heading={reviewCopy.crumbLeaf}
          busy={busy}
          onCancel={() => setSendingBack(false)}
          onConfirm={(decision) => decide.mutate({ kind: 'reject', ...decision })}
        />
      </>
    );
  }

  if (accepted !== null && person.lifecycle === 'candidate') {
    return (
      <>
        {notice}
        <MoveToOnboarding
          person={person}
          offer={signedOfferSummary(accepted, acceptedStep, { moment: dayAndTime, day: formatRecordedCalendarDate })}
          firstName={firstName}
          roleLabel={roleLabel}
          canEdit={canEdit}
          busy={busy}
          onMove={(choice) => move.mutate(choice)}
          onCancel={dismiss}
        />
        {sentWindow}
      </>
    );
  }

  /*
   * PAST CANDIDATE: the two rows are done, and this section says what happened (#346).
   *
   * This is where a candidate lands the instant a move succeeds. It used to render the notice and
   * nothing else, so the older workflow panel and a general-purpose stage dropdown showed through
   * underneath — neither of which is in any frame for this screen. The notice still comes first: it
   * is exactly what must not disappear here, because a move that half-worked reports itself in it.
   *
   * `offer` is null for someone who reached Onboarding without one on record — a paper hire, or a
   * move made before offers were tracked. The card then shows the stage alone rather than an empty
   * offer row.
   */
  if (person.lifecycle !== 'candidate') {
    return (
      <>
        {notice}
        <OfferOutcome
          person={person}
          firstName={firstName}
          offer={accepted === null ? null : signedOfferSummary(accepted, acceptedStep, { moment: dayAndTime, day: formatRecordedCalendarDate })}
          /*
            Formatted here, where this screen's formatters already live, so `OfferOutcome` takes no
            clock and no locale of its own — the same reason `signedOfferSummary` is handed its own.
            A person can reach Onboarding before anybody types a first day, so the absent case has
            its own sentence rather than printing "Hired on null".
          */
          hireDateLine={hireDateLine}
          /*
            Sending mail is a write: a viewer gets the banner and no button.

            AND NOT WHILE 2.4 IS OPEN, for the same reason `notice` and the route below are
            suppressed then: this button sits underneath that modal, and the refusal it can raise is
            reported through `notice`, which 2.4 hides. A review of #396 issued a real send from here
            with 2.4 open and watched the refusal go nowhere at all — an action whose only error
            channel is covered up must not be reachable.
          */
          onResendWelcome={canEdit && welcomeTemplate !== null && sent === null ? () => resend.mutate() : undefined}
          resending={resend.isPending}
          /*
            NOT WHILE 2.4 IS OPEN. 2.4 carries the same "Go to Documents & checklist" action, and
            after the lifecycle flip both it and 2.5 are mounted — so the route appeared TWICE, two
            buttons with one accessible name, one in a modal over the other. A test caught it by
            matching multiple elements.

            Suppressed on 2.5 rather than removed from 2.4: 2.4 is the transient confirmation and its
            link is the whole reason it exists, while 2.5 offers the route again for anyone who opens
            the record later. Closing 2.4 brings it back here. Same reasoning as `notice` above.
          */
          onGoToDocuments={sent === null ? onGoToDocuments : undefined}
        />
        {sentWindow}
      </>
    );
  }

  // A candidate with no signed offer yet: the offer panel below is the whole of this section.
  return notice;
}
